import { ChangeEvent, FC, FormEvent, useMemo, useState } from 'react';
import { Button, TextField, Autocomplete } from '../../../../ui';
import { FormControlLabel, IconButton, InputAdornment, Radio, RadioGroup, Stack, Box } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useGetAllBuyerCompanies, useGetAllBuyerCompanyOptions } from '../../../../hooks';
import { backendServices } from '../../../../services';

interface IProps {
    email: string;
    emailError: boolean;
    handleEmailChange: (event: ChangeEvent) => void;
    password: string;
    passwordError: boolean;
    handlePasswordChange: (event: ChangeEvent) => void;
    companyId: string;
    companyIdError: boolean;
    handleCompanyIdChange: (companyId: string) => void;
    handleSignIn: (event: FormEvent) => void;
    handleForgotPassword: () => void;
    isBuyer?: boolean;
    useCardLayout?: boolean;
}

type CompanyInputMode = 'select' | 'manual';

const fieldSx = {
    pb: 1,
    '.MuiInputLabel-shrink': {
        top: -8,
    },
};

const cardFieldSx = {
    ...fieldSx,
    width: '100%',
};

function CompanyDropdownField({
    companyId,
    companyIdError,
    handleCompanyIdChange,
    buyerCompanies,
    isLoading,
    error,
    fullWidth,
}: {
    companyId: string;
    companyIdError: boolean;
    handleCompanyIdChange: (companyId: string) => void;
    buyerCompanies: backendServices.ViewModels.CompanyViewModel[];
    isLoading: boolean;
    error: unknown;
    fullWidth?: boolean;
}) {
    const buyerCompanyOptions = useGetAllBuyerCompanyOptions({ buyerCompanies });

    const autocompleteOptions = useMemo(
        () => buyerCompanyOptions.map((opt) => ({ label: opt.label, id: opt.value })),
        [buyerCompanyOptions]
    );

    const selectedOption = useMemo(() => {
        if (!companyId) {
            return null;
        }
        return autocompleteOptions.find((opt) => opt.id === companyId) ?? null;
    }, [companyId, autocompleteOptions]);

    const hasError = !!error;

    return (
        <Box sx={fullWidth ? { width: '100%' } : undefined}>
            <Autocomplete
                id="transcepta-login-company-id"
                label="Company"
                value={selectedOption}
                onChange={(option) => handleCompanyIdChange(option?.id ?? '')}
                options={autocompleteOptions}
                error={companyIdError || hasError}
                helperText={hasError ? 'Failed to load companies. Use "Enter company ID" instead.' : undefined}
                disabled={hasError}
                loading={isLoading}
                loadingText="Loading companies..."
                noOptionsText="No companies found"
                stacked
            />
        </Box>
    );
}

function CompanyTextField({
    companyId,
    companyIdError,
    handleCompanyIdChange,
    fullWidth,
}: {
    companyId: string;
    companyIdError: boolean;
    handleCompanyIdChange: (companyId: string) => void;
    fullWidth?: boolean;
}) {
    return (
        <TextField
            stacked
            id="transcepta-login-company-id"
            label="Company ID"
            type="text"
            color={fullWidth ? 'primary' : 'secondary'}
            value={companyId}
            error={companyIdError}
            onChange={(e) => handleCompanyIdChange((e.target as HTMLInputElement).value)}
            sx={fullWidth ? cardFieldSx : fieldSx}
        />
    );
}

const CompanyField = ({
    isBuyer,
    companyId,
    companyIdError,
    handleCompanyIdChange,
    useCardLayout,
}: {
    isBuyer: boolean;
    companyId: string;
    companyIdError: boolean;
    handleCompanyIdChange: (companyId: string) => void;
    useCardLayout?: boolean;
}) => {
    const { isLoading, error, data: buyerCompanies } = useGetAllBuyerCompanies({ enabled: isBuyer });
    const [mode, setMode] = useState<CompanyInputMode>('select');

    const handleModeChange = (_: ChangeEvent<HTMLInputElement>, value: string) => {
        setMode(value as CompanyInputMode);
        handleCompanyIdChange('');
    };

    const fw = !!useCardLayout;

    if (!isBuyer) {
        return (
            <CompanyTextField
                companyId={companyId}
                companyIdError={companyIdError}
                handleCompanyIdChange={handleCompanyIdChange}
                fullWidth={fw}
            />
        );
    }

    return (
        <>
            <RadioGroup row value={mode} onChange={handleModeChange} sx={{ mb: 1 }}>
                <FormControlLabel value="select" control={<Radio size="small" />} label="Select a company" />
                <FormControlLabel value="manual" control={<Radio size="small" />} label="Enter company ID" />
            </RadioGroup>
            {mode === 'select' ? (
                <CompanyDropdownField
                    companyId={companyId}
                    companyIdError={companyIdError}
                    handleCompanyIdChange={handleCompanyIdChange}
                    buyerCompanies={buyerCompanies ?? []}
                    isLoading={isLoading}
                    error={error}
                    fullWidth={fw}
                />
            ) : (
                <CompanyTextField
                    companyId={companyId}
                    companyIdError={companyIdError}
                    handleCompanyIdChange={handleCompanyIdChange}
                    fullWidth={fw}
                />
            )}
        </>
    );
};

const InternalLoginForm: FC<IProps> = ({
    email,
    emailError,
    handleEmailChange,
    password,
    passwordError,
    handlePasswordChange,
    companyId,
    companyIdError,
    handleCompanyIdChange,
    handleSignIn,
    handleForgotPassword,
    isBuyer = false,
    useCardLayout = false,
}) => {
    const [showPassword, setShowPassword] = useState(false);

    if (useCardLayout) {
        return (
            <form onSubmit={handleSignIn}>
                <Stack spacing={2}>
                    <TextField
                        stacked
                        id="transcepta-login-email"
                        label="Email"
                        type="email"
                        value={email}
                        error={emailError}
                        onChange={handleEmailChange}
                        sx={cardFieldSx}
                    />
                    <TextField
                        stacked
                        id="transcepta-login-password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        error={passwordError}
                        onChange={handlePasswordChange}
                        sx={cardFieldSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword((v) => !v)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <CompanyField
                        isBuyer={isBuyer}
                        companyId={companyId}
                        companyIdError={companyIdError}
                        handleCompanyIdChange={handleCompanyIdChange}
                        useCardLayout
                    />
                    <Button
                        type="button"
                        variant="text"
                        fullWidth
                        onClick={handleForgotPassword}
                        testid="login-page-forgot-password-button"
                        sx={{
                            textTransform: 'none',
                            justifyContent: 'center',
                            py: 1,
                        }}
                    >
                        I forgot my password
                    </Button>
                    <Button type="submit" variant="default" fullWidth>
                        Sign In
                    </Button>
                </Stack>
            </form>
        );
    }

    return (
        <form onSubmit={handleSignIn}>
            <TextField
                stacked
                id="transcepta-login-email"
                label="Email"
                type="email"
                color="secondary"
                value={email}
                error={emailError}
                onChange={handleEmailChange}
                sx={fieldSx}
            />
            <TextField
                stacked
                id="transcepta-login-password"
                label="Password"
                type="password"
                color="secondary"
                value={password}
                error={passwordError}
                onChange={handlePasswordChange}
                sx={fieldSx}
            />
            <CompanyField
                isBuyer={isBuyer}
                companyId={companyId}
                companyIdError={companyIdError}
                handleCompanyIdChange={handleCompanyIdChange}
            />
            <Stack direction="row" justifyContent="space-between">
                <Button type="submit">Sign In</Button>
                <Button variant="text" onClick={handleForgotPassword}>
                    I forgot my password
                </Button>
            </Stack>
        </form>
    );
};

export default InternalLoginForm;
